import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useMemo, useState } from "react";
import Avatar from "components/Avatar";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import { Checkbox } from "@mui/material";
import DropdownMui from "components/DropdownMui";
import ConfirmModal from "components/ConfirmModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import CardIcon from "assets/icons/credit_card.svg?react";
import { ENotificationType, notification } from "utils/notification";
import workspaceService from "api/workspaceService";
import { EAppVariant } from "dto/EAppVariant";
import { EAppExtension } from "dto/EAppExtension";
import { ESubscriptionMode } from "dto/ISubscriptionsView";
import {
  EXTENSION_ORDER,
  EXTENSION_LABEL,
  PLAN_DISK_GB,
  PLAN_LABEL,
  PLAN_ORDER,
  PLAN_PRICE,
  formatPrice,
  getExtensionPrice,
  getMonthlyCost,
} from "utils/appPlans";

/** Stawka VAT doliczana do cen netto z cennika przy pobraniu z karty. */
const VAT_RATE = 0.23;

const MemberSubscriptionPanel: FC<Props> = ({
  member,
  mode,
  workspaceId,
  onBack,
  onSaved,
  onStage,
  onRequireCard,
}) => {
  const [variant, setVariant] = useState<EAppVariant>(member.Variant);
  const [extensions, setExtensions] = useState<EAppExtension[]>(
    member.Extensions ?? []
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const { mutate, isLoading } = workspaceService.useUpdateSubscription();

  const isPremium = variant === EAppVariant.Premium;
  const monthlyCost = useMemo(
    () => getMonthlyCost(variant, extensions),
    [variant, extensions]
  );

  // Klasyfikacja zmiany (pakiet i moduły osobno) + szacowana proporcjonalna różnica do pobrania teraz.
  // Wartość ostateczną wylicza BE; tu prezentujemy szacunek wg dni pozostałych do końca miesiąca.
  const pricing = useMemo(() => {
    const effectiveExtensions = isPremium ? [] : extensions;
    const oldTier = PLAN_ORDER.indexOf(member.Variant);
    const newTier = PLAN_ORDER.indexOf(variant);
    const planUpgrade = newTier > oldTier;
    const planDowngrade = newTier < oldTier;

    const addedModules = effectiveExtensions.filter(
      (e) => !(member.Extensions ?? []).includes(e)
    );
    const removedModules = isPremium
      ? []
      : (member.Extensions ?? []).filter((e) => !effectiveExtensions.includes(e));

    const now = new Date();
    const daysInMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    ).getDate();
    const factor = (daysInMonth - now.getDate() + 1) / daysInMonth;

    // Plan obowiązujący przez pozostałe dni mc do wyceny dodanych modułów: upgrade → nowy, inaczej bieżący.
    const remainingPlan = planUpgrade ? variant : member.Variant;

    const planDelta = planUpgrade
      ? (PLAN_PRICE[variant] - PLAN_PRICE[member.Variant]) * factor
      : 0;
    const moduleDelta = addedModules.reduce(
      (sum, ext) => sum + getExtensionPrice(ext, remainingPlan) * factor,
      0
    );
    const deltaEstimate = Math.max(0, planDelta + moduleDelta);
    // Cennik jest NETTO; z karty pobieramy BRUTTO (VAT 23%) – prezentujemy kwotę faktycznie pobieraną.
    const deltaEstimateGross = deltaEstimate * (1 + VAT_RATE);

    return {
      deltaEstimate,
      deltaEstimateGross,
      hasUpgrade: planUpgrade || addedModules.length > 0,
      hasDowngrade: planDowngrade || removedModules.length > 0,
    };
  }, [member, variant, extensions, isPremium]);

  const activeUntil = member.ActiveUntil
    ? new Date(member.ActiveUntil).toLocaleDateString("pl-PL")
    : null;

  const planList = PLAN_ORDER.map((v) => ({
    value: v,
    name: PLAN_LABEL[v],
  }));

  const toggleExtension = (ext: EAppExtension, checked: boolean) => {
    setExtensions((prev) =>
      checked ? [...prev, ext] : prev.filter((e) => e !== ext)
    );
  };

  const doSave = () => {
    // Tryb zespołowy ze stagingiem – nie zapisujemy od razu, tylko odkładamy zmianę na listę.
    if (onStage) {
      onStage(variant, isPremium ? [] : extensions);
      onBack();
      return;
    }

    mutate(
      {
        WorkspaceId: mode === ESubscriptionMode.Team ? workspaceId : null,
        MemberUserId:
          mode === ESubscriptionMode.Team ? member.UserId : null,
        Variant: variant,
        // Premium zawiera moduły w cenie — BE i tak ignoruje listę.
        Extensions: isPremium ? [] : extensions,
      },
      {
        onSuccess: (resp) => {
          // H4: gdy zmiana wymaga płatności, a brak karty → przenosimy do dodania karty.
          if (resp?.data?.RequiresPaymentMethod && onRequireCard) {
            notification({
              type: ENotificationType.INFO,
              title: "Dodaj kartę",
              text: "Aby aktywować pakiet, dodaj kartę płatniczą — płatność zostanie pobrana automatycznie.",
            });
            onRequireCard();
            return;
          }
          notification({
            type: ENotificationType.SUCCESS,
            title: "Subskrypcja",
            text: "Zapisano zmiany subskrypcji.",
          });
          onSaved();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Subskrypcja",
            text: "Nie udało się zapisać subskrypcji.",
          }),
      }
    );
  };

  const handleSave = () => {
    // Przy stagingu (zespół) pomijamy dialog per-członek — potwierdzenie jest przy zbiorczym Zapisz.
    if (variant !== member.Variant && !onStage) {
      setShowConfirm(true);
      return;
    }
    doSave();
  };

  const confirmText = useMemo(() => {
    const who =
      mode === ESubscriptionMode.Team
        ? `plan użytkownika ${member.Label}`
        : "swój plan";
    const newTier = PLAN_ORDER.indexOf(variant);
    const oldTier = PLAN_ORDER.indexOf(member.Variant);

    if (newTier > oldTier) {
      // Upgrade – proporcjonalna różnica pobierana teraz, aktywacja po płatności.
      return `Zmieniasz ${who} na wyższy (${PLAN_LABEL[variant]}). Pobierzemy proporcjonalną różnicę (≈ ${formatPrice(
        pricing.deltaEstimateGross
      )} z VAT 23%) za pozostałe dni bieżącego miesiąca; nowy pakiet aktywuje się po udanej płatności.`;
    }

    // Downgrade – bieżący pakiet działa do końca okresu, niższy wchodzi z kolejnym miesiącem.
    return `Zmieniasz ${who} na niższy (${PLAN_LABEL[variant]}). Bieżący pakiet (${PLAN_LABEL[member.Variant]}) pozostaje aktywny do końca bieżącego okresu — od kolejnego miesiąca wejdzie ${PLAN_LABEL[variant]} (${formatPrice(
      getMonthlyCost(variant, isPremium ? [] : extensions)
    )} / mies.).`;
  }, [member, variant, extensions, isPremium, mode, pricing]);

  return (
    <div className={styles.panel}>
      <div className={styles.breadcrumb}>
        <FontAwesomeIcon
          icon={faArrowLeft}
          className={styles.back}
          onClick={onBack}
        />
        <span className={styles.muted}>Płatności</span>
        <FontAwesomeIcon icon={faChevronLeft} className={styles.sep} />
        <span>{member.Label}</span>
      </div>

      <div className={styles.summary}>
        <div className={styles.who}>
          <Avatar className={styles.avatar} name={member.Label} size="small" />
          <span>{member.Label}</span>
        </div>
        <div className={styles.fee}>
          <span className={styles.muted}>Opłata miesięczna</span>
          <span className={styles.divider}>|</span>
          <span className={styles.amount}>{formatPrice(monthlyCost)}</span>
        </div>
      </div>

      <div className={styles.vatNote}>
        Podane ceny są cenami netto — zostanie do nich doliczony podatek VAT 23%.
      </div>

      {activeUntil && (
        <div className={styles.notice}>
          Masz już zaplanowaną zmianę subskrypcji — bieżąca konfiguracja aktywna
          do {activeUntil}.
        </div>
      )}

      {pricing.deltaEstimate > 0 && (
        <div className={styles.notice}>
          Do pobrania teraz (proporcjonalnie do końca miesiąca): ≈{" "}
          <strong>{formatPrice(pricing.deltaEstimateGross)}</strong> (z VAT 23%).
          Nowa konfiguracja aktywuje się po udanej płatności.
        </div>
      )}

      {pricing.hasDowngrade && (
        <div className={styles.notice}>
          Obniżki (niższy pakiet / usunięte moduły) wejdą w życie z początkiem
          kolejnego miesiąca — bieżąca konfiguracja działa do końca bieżącego
          okresu.
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Plany</div>
        <div className={styles.sectionHint}>Wybierz plan dla użytkownika.</div>
        <DropdownMui
          className={styles.dropdown}
          label="Wybrany plan"
          list={planList}
          defaultValue={variant}
          onChange={(box) => setVariant(box.value as EAppVariant)}
        />
      </div>

      <div className={styles.divLine}></div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Rozszerzenia</div>
        <div className={styles.sectionHint}>
          Dokup moduły rozszerzające możliwości pakietu. W pakiecie Premium
          moduły są zawarte w cenie.
        </div>
        {EXTENSION_ORDER.map((ext) => {
          const checked = isPremium || extensions.includes(ext);
          const price = getExtensionPrice(ext, variant);
          return (
            <div key={ext} className={styles.checkRow}>
              <Checkbox
                size="small"
                color="primary"
                checked={checked}
                disabled={isPremium}
                onChange={(e) => toggleExtension(ext, e.target.checked)}
                sx={{ padding: 0 }}
              />
              <span className={styles.checkLabel}>{EXTENSION_LABEL[ext]}</span>
              <span className={styles.checkPrice}>
                {isPremium ? "w cenie" : `+ ${formatPrice(price)} / mies.`}
              </span>
            </div>
          );
        })}
      </div>

      <div className={styles.divLine}></div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Przestrzeń dyskowa</div>
        <div className={styles.sectionHint}>
          Pojemność dysku wynika z wybranego pakietu.
        </div>
        <div className={styles.readonlyField}>
          <span className={styles.muted}>Pojemność dysku</span>
          <span className={styles.amount}>{PLAN_DISK_GB[variant]} GB</span>
        </div>
      </div>

      <div className={styles.actions}>
        <HollowButton text="Anuluj" onClick={onBack} />
        <Button text="Zapisz" onClick={handleSave} loading={isLoading} />
      </div>

      {showConfirm && (
        <ConfirmModal
          Icon={CardIcon}
          title="Zmiana planu"
          text={confirmText}
          cancelLabel="Anuluj"
          acceptLabel="Zatwierdź"
          showDontShowNextTime={false}
          onCancel={() => setShowConfirm(false)}
          onAccept={() => {
            setShowConfirm(false);
            doSave();
          }}
        />
      )}
    </div>
  );
};

export default MemberSubscriptionPanel;
